import React, { useMemo, useRef, useState } from 'react'
import { createColumnHelper, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

import styles from './styles.module.scss'

import Checkbox from '../../Core/Checkbox/Checkbox2'
import Table from '../Table'
import PreviewPDF from '../PreviewPDF'
import InvoiceStatus from '../InvoiceStatus'

import { fetchExpenseFile } from '../../../api'

const FileIcon = (props) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={18}
      height={20}
      fill="none"
      {...props}
    >
      <path
        fill="#808F94"
        d="m15.102 10.297-6.81 6.77c-1.77 1.77-4.152 1.594-5.672.065-1.529-1.51-1.705-3.86.065-5.628l9.294-9.23c1.066-1.06 2.632-1.208 3.65-.204 1.01 1.023.863 2.57-.194 3.63l-9.08 9.028c-.455.47-1.01.331-1.326.018-.315-.322-.444-.857.01-1.326l6.347-6.292c.278-.286.296-.691.028-.958-.269-.258-.677-.24-.955.037l-6.365 6.328c-.946.94-.908 2.377-.075 3.206.909.903 2.28.884 3.234-.064l9.118-9.065c1.733-1.723 1.659-3.989.148-5.49-1.473-1.465-3.79-1.576-5.522.147l-9.34 9.295c-2.289 2.275-2.113 5.527-.047 7.581 2.067 2.046 5.338 2.22 7.626-.046l6.857-6.816c.269-.268.269-.756-.01-1.005-.268-.285-.703-.248-.981.019Z"
      />
    </svg>
  )
}

const getValueByPath = (item, path) => path
  .split('.')
  .reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), item)

const getDisplayName = (item, keys, fallback) => {
  for (const key of keys) {
    const value = getValueByPath(item, key)

    if (value !== undefined && value !== null && value !== '') {
      return value
    }
  }

  return fallback
}

const formatMoney = value => {
  const parsed = Number(value)

  if (Number.isNaN(parsed)) {
    return '0.00'
  }

  return parsed.toFixed(2)
}

const Expenses = ({companyId, lockEntriesOnIssue, expenses, loading, selected, onSelect}) => {
  const { t } = useTranslation()

  const columnHelper = createColumnHelper()
  const [sorting, setSorting] = useState([])

  const previewPdfRef = useRef(null)

  const rowSelection = useMemo(() => {
    return Object.keys(selected || {}).reduce((acc, id) => {
      acc[id] = true
      return acc
    }, {})
  }, [selected])

  const expensesById = useMemo(() => {
    return (Array.isArray(expenses) ? expenses : []).reduce((acc, expense) => {
      acc[String(expense.id)] = expense
      return acc
    }, {})
  }, [expenses])

  const handleRowSelectionChange = updater => {
    const nextRowSelection = typeof updater === 'function'
      ? updater(rowSelection)
      : updater

    const nextSelectedExpenses = Object.keys(nextRowSelection).reduce((acc, id) => {
      if (nextRowSelection[id]) {
        acc[id] = expensesById[id]
      }

      return acc
    }, {})

    onSelect(nextSelectedExpenses)
  }

  const handleReceiptClick = async (e, row) => {
    e.preventDefault()
    const base64 = await fetchExpenseFile(companyId, row.place_id, row.id)
    previewPdfRef.current.open({ base64 })

  }

  const columns = useMemo(() => ([
    columnHelper.display({
      id: 'select',
      enableSorting: false,
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
    }),
    columnHelper.accessor(row => moment(row.invoice_date || row.created_at).valueOf(), {
      id: 'date',
      header: t('Date'),
      cell: ({ row }) => {
        const date = row.original.invoice_date || row.original.created_at

        if (!date) {
          return '-'
        }

        return moment(date).format('YYYY-MM-DD')
      },
      enableSorting: true,
    }),
    columnHelper.accessor(row => row.expense_type?.name || '', {
      id: 'type',
      header: t('Type'),
      cell: ({ row }) => {
        if (row.original.expense_type) {
          return (
            <div
              className={styles.typeBadge}
              style={{
                '--type-badge-color': row.original.expense_type.color || '#34C759',
                '--type-badge-background': `${row.original.expense_type.color || '#34C759'}14`
              }}
            >
              <span className={styles.typeBadgeDot} />
              <span>{row.original.expense_type.name}</span>
            </div>
          )
        }
        return null
      },
      enableSorting: true,
    }),
    columnHelper.accessor(row => getDisplayName(
      row,
      ['place.name', 'place_name'],
      row.place_id || '',
    ), {
      id: 'place',
      header: t('Place'),
      cell: ({ row }) => getDisplayName(
        row.original,
        ['place.name', 'place_name'],
        row.original.place_id,
      ),
      enableSorting: true,
    }),
    columnHelper.accessor('notes', {
      id: 'notes',
      enableSorting: false,
      header: t('Description'),
    }),
    columnHelper.accessor('vendor_name', {
      id: 'vendor',
      enableSorting: false,
      header: t('Supplier'),
    }),
    columnHelper.accessor(row => Number(row.amount ?? 0), {
      id: 'amount',
      header: t('Amount'),
      cell: ({ row }) => `${formatMoney(row.original.amount)} ${row.original.currency_code || ''}`.trim(),
      enableSorting: true,
    }),
    columnHelper.display({
      id: 'receipt',
      enableSorting: false,
      header: t('Receipt'),
      cell: ({ row }) => {
        if (!row.original.file) {
          return '-'
        }

        return (
          <a
            className={styles.receiptLink}
            href={row.original.file}
            onClick={e => handleReceiptClick(e, row.original)}
            rel='noreferrer'
          >
            <FileIcon />
          </a>
        )
      },
    }),
    columnHelper.accessor('billing_status', {
      id: 'status',
      header: t('Status'),
      cell: ({ getValue, row }) => {
        const status = getValue() === 'sent' ? 'billed' : getValue()
        return <InvoiceStatus status={status} dueDate={row.original.invoice_item?.invoice?.due_date} />
      },
      enableSorting: true,
    }),
  ]), [columnHelper, t])

  const table = useReactTable({
    data: Array.isArray(expenses) ? expenses : [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: row => String(row.id),
    state: {
      rowSelection,
      sorting,
    },
    enableRowSelection: row => !lockEntriesOnIssue || !['sent', 'paid'].includes(row.original?.billing_status),
    onRowSelectionChange: handleRowSelectionChange,
    onSortingChange: setSorting,
  })

  return (
    <>
      <Table table={table} columns={columns} loading={loading} />
      <PreviewPDF ref={previewPdfRef} />
    </>
  )
}

export default Expenses
